if (!customElements.get('upcoming-events')) {
  customElements.define(
    'upcoming-events',
    class UpcomingEvents extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.locale = document.documentElement.lang || undefined;
        this.isEditor = this.dataset.editor === 'true';
        this.newTab = this.dataset.newTab === 'true';
        this.today = this.toKey(new Date());
        this.events = this.readEvents();

        const now = new Date();
        this.month = new Date(now.getFullYear(), now.getMonth(), 1);

        this.toggle = this.querySelector('[data-toggle]');
        this.panel = this.querySelector('[data-panel]');

        this.toggle.addEventListener('click', () => this.setOpen(this.panel.hidden));
        this.querySelector('[data-prev]').addEventListener('click', () => this.shiftMonth(-1));
        this.querySelector('[data-next]').addEventListener('click', () => this.shiftMonth(1));

        this.renderWeekdays();
        this.renderNext();
        this.renderMonth();
        this.setOpen(this.dataset.startOpen === 'true');
      }

      readEvents() {
        let events = [];
        try {
          events = JSON.parse(this.querySelector('[data-events]').textContent);
        } catch (error) {
          console.error('Upcoming events: could not read event data', error);
        }
        return events
          .filter((event) => /^\d{4}-\d{2}-\d{2}$/.test(event.date))
          .sort((a, b) => a.date.localeCompare(b.date) || a.name.localeCompare(b.name));
      }

      toKey(date) {
        const pad = (n) => String(n).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
      }

      fromKey(key) {
        const [year, month, day] = key.split('-').map(Number);
        return new Date(year, month - 1, day);
      }

      formatDate(key, options) {
        return this.fromKey(key).toLocaleDateString(this.locale, options);
      }

      isPast(event) {
        return event.date < this.today;
      }

      isSafeUrl(url) {
        return typeof url === 'string' && /^(https?:\/\/|\/)/i.test(url);
      }

      setOpen(open) {
        this.panel.hidden = !open;
        this.toggle.setAttribute('aria-expanded', String(open));
        this.toggle.querySelector('[data-label-closed]').hidden = open;
        this.toggle.querySelector('[data-label-open]').hidden = !open;
      }

      shiftMonth(step) {
        this.month = new Date(this.month.getFullYear(), this.month.getMonth() + step, 1);
        this.renderMonth();
      }

      renderWeekdays() {
        const row = this.querySelector('[data-weekdays]');
        // Jan 1, 2023 was a Sunday.
        for (let i = 0; i < 7; i++) {
          const day = new Date(2023, 0, 1 + i);
          const th = document.createElement('th');
          th.scope = 'col';
          th.textContent = day.toLocaleDateString(this.locale, { weekday: 'short' });
          row.appendChild(th);
        }
      }

      renderNext() {
        const list = this.querySelector('[data-next-list]');
        const upcoming = this.events.filter((event) => !this.isPast(event)).slice(0, 3);
        list.replaceChildren();

        if (!upcoming.length) {
          const empty = document.createElement('span');
          empty.className = 'upcoming-events__next-empty';
          empty.textContent = this.dataset.emptyLabel;
          list.appendChild(empty);
          return;
        }

        upcoming.forEach((event) => {
          const item = document.createElement('span');
          item.className = 'upcoming-events__next-item';

          const date = document.createElement('span');
          date.className = 'upcoming-events__next-date';
          date.textContent = this.formatDate(event.date, { weekday: 'short', month: 'short', day: 'numeric' });

          const name = document.createElement('span');
          name.className = 'upcoming-events__next-name';
          name.textContent = event.name;

          item.append(date, name);
          list.appendChild(item);
        });
      }

      renderMonth() {
        const year = this.month.getFullYear();
        const month = this.month.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const leadingBlanks = this.month.getDay();

        this.querySelector('[data-month-label]').textContent = this.month.toLocaleDateString(this.locale, {
          month: 'long',
          year: 'numeric',
        });

        const eventsByDate = {};
        this.events.forEach((event) => {
          (eventsByDate[event.date] = eventsByDate[event.date] || []).push(event);
        });

        const body = this.querySelector('[data-grid-body]');
        body.replaceChildren();
        let row = document.createElement('tr');

        for (let i = 0; i < leadingBlanks; i++) {
          row.appendChild(this.createCell());
        }

        for (let day = 1; day <= daysInMonth; day++) {
          if (row.children.length === 7) {
            body.appendChild(row);
            row = document.createElement('tr');
          }
          const key = this.toKey(new Date(year, month, day));
          row.appendChild(this.createCell(day, key, eventsByDate[key] || []));
        }

        while (row.children.length < 7) row.appendChild(this.createCell());
        body.appendChild(row);

        const monthPrefix = this.toKey(this.month).slice(0, 7);
        const monthList = this.querySelector('[data-month-list]');
        monthList.replaceChildren(
          ...this.events
            .filter((event) => event.date.startsWith(monthPrefix))
            .map((event) => this.createEvent(event, true))
        );
      }

      createCell(day, key, events = []) {
        const cell = document.createElement('td');
        cell.className = 'upcoming-events__day';
        if (!day) {
          cell.classList.add('upcoming-events__day--empty');
          return cell;
        }

        if (key === this.today) cell.classList.add('upcoming-events__day--today');
        if (key < this.today) cell.classList.add('upcoming-events__day--past');
        if (events.length) cell.classList.add('upcoming-events__day--has-events');

        const header = document.createElement('div');
        header.className = 'upcoming-events__day-header';

        const number = document.createElement('span');
        number.className = 'upcoming-events__day-number';
        number.textContent = day;
        header.appendChild(number);

        if (this.isEditor && this.dataset.addUrl) {
          const add = document.createElement('a');
          add.className = 'upcoming-events__admin-link upcoming-events__add';
          add.href = this.dataset.addUrl;
          add.target = '_blank';
          add.rel = 'noopener';
          add.textContent = '+';
          const label = `Add event on ${this.formatDate(key, { month: 'long', day: 'numeric', year: 'numeric' })}`;
          add.title = `${label} (set this date in the form)`;
          add.setAttribute('aria-label', label);
          header.appendChild(add);
        }

        cell.appendChild(header);

        if (events.length) {
          const list = document.createElement('ul');
          list.className = 'upcoming-events__day-events list-unstyled';
          events.forEach((event) => list.appendChild(this.createEvent(event, false)));
          cell.appendChild(list);
        }

        return cell;
      }

      createEvent(event, showDate) {
        const item = document.createElement('li');
        item.className = 'upcoming-events__event';
        const past = this.isPast(event);
        if (past) item.classList.add('upcoming-events__event--past');

        if (showDate) {
          const date = document.createElement('span');
          date.className = 'upcoming-events__event-date';
          date.textContent = this.formatDate(event.date, { weekday: 'short', month: 'short', day: 'numeric' });
          item.appendChild(date);
        }

        let name;
        if (this.isSafeUrl(event.url)) {
          name = document.createElement('a');
          name.href = event.url;
          if (this.newTab) {
            name.target = '_blank';
            name.rel = 'noopener';
          }
        } else {
          name = document.createElement('span');
        }
        name.className = 'upcoming-events__event-name';
        name.textContent = event.name;
        item.appendChild(name);

        if (past) {
          const badge = document.createElement('span');
          badge.className = 'upcoming-events__passed';
          badge.textContent = this.dataset.passedLabel;
          item.appendChild(badge);
        }

        if (this.isEditor && event.editUrl) {
          const edit = document.createElement('a');
          edit.className = 'upcoming-events__admin-link upcoming-events__edit';
          edit.href = event.editUrl;
          edit.target = '_blank';
          edit.rel = 'noopener';
          edit.textContent = 'Edit';
          edit.setAttribute('aria-label', `Edit ${event.name}`);
          item.appendChild(edit);
        }

        return item;
      }
    }
  );
}
